import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import {
  sectionListRequest,
  sectionUpdateOrderRequest,
  deleteSectionRequest,
} from "../../../../store/slices/customFieldSlice";
import List from "../../../common/list";
import { TabMenu } from "primereact/tabmenu";
import * as ListCellRenders from "../../../common/list/ListCellRenders";

const CustomFieldList = ({
  action,
  customFieldSections,
  error,
  labels,
  listSections,
  requesting,
  user,
  updateOrder,
  deleteSectionRequest,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const [activeTab, setActiveTab] = useState(0);

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.CreateScope
  );

  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.UpdateScope
  );

  const canDelete = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.DeleteScope
  );

  useEffect(() => {
    listSections();
  }, []);

  useEffect(() => {
    if (
      (action === "section-update-order" || action === "section-delete") &&
      !requesting &&
      !error
    ) {
      listSections();
    }
  }, [error, action, requesting, listSections]);

  const handleAddCustomFieldSection = () => {
    navigate(RouteConstants.APP_ROUTE_ADMINISTRATION_CUSTOM_FIELD_SECTIONS_ADD);
  };

  const handleSectionOrderChange = (section, direction) => {
    const currentSectionIndex = customFieldSections.findIndex(
      (item) => item.id === section.id
    );
    const nextSectionIndex =
      direction === "up" ? currentSectionIndex - 1 : currentSectionIndex + 1;
    const currentSection = customFieldSections[currentSectionIndex];
    const nextSection = customFieldSections[nextSectionIndex];
    const payload = {
      sectionIdOne: currentSection.id,
      orderOne: nextSection.order,
      sectionIdTwo: nextSection.id,
      orderTwo: currentSection.order,
    };
    updateOrder(payload);
  };

  const onDeleteAction = (data) => {
    confirmDialog({
      message: intl.formatMessage(
        { id: "delete_object_message" },
        {
          name: data.name,
          object: intl.formatMessage({ id: "custom_field_section" }),
        }
      ),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => {
        deleteSectionRequest({ id: data.id });
      },
      reject: () => {},
    });
  };

  let columns = [
    {
      field: "name",
      header: "section_name",
    },
  ];

  if (canUpdate && customFieldSections.length > 1) {
    columns.push({
      field: "order",
      header: "order",
      body: (data) => {
        if (data.order === 1) {
          return (
            <div className="grid gap-2">
              <Button
                type="button"
                icon="pi pi-arrow-down"
                onClick={() => handleSectionOrderChange(data, "down")}
              />
            </div>
          );
        } else if (data.order === customFieldSections.length) {
          return (
            <div className="grid gap-2">
              <Button
                type="button"
                icon="pi pi-arrow-up"
                onClick={() => handleSectionOrderChange(data, "up")}
              />
            </div>
          );
        } else {
          return (
            <div className="grid gap-2">
              <Button
                type="button"
                icon="pi pi-arrow-down"
                onClick={() => handleSectionOrderChange(data, "down")}
              />
              <Button
                type="button"
                icon="pi pi-arrow-up"
                onClick={() => handleSectionOrderChange(data, "up")}
              />
            </div>
          );
        }
      },
    });
  }

  columns.push({
    field: "actions",
    headerIcon: "pi-cog",
    align: "center",
    body: ListCellRenders.OtherActions([
      {
        label: "delete",
        icon: "pi-trash",
        isAllowed: canDelete,
        action: (data) => onDeleteAction(data),
      },
    ]),
  });

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "custom_fields" })}
          </div>
          <div className="edexlms-page-actions">
            <Button
              icon="pi pi-plus"
              onClick={handleAddCustomFieldSection}
              size="small"
              label={intl.formatMessage({ id: "add_custom_field_section" })}
              type="button"
              disabled={!canAdd}
              tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
              tooltip={
                canAdd ? "" : intl.formatMessage({ id: "no_permission" })
              }
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <TabMenu
            className="edexlms-tab-menu"
            model={[
              {
                label: intl.formatMessage({ id: "user_profile" }),
                command: () => {
                  if (activeTab !== 0) {
                    setActiveTab(0);
                  }
                },
              },
            ]}
          />
          {(activeTab === 0 && (
            <>
              <List
                pageQuery={{
                  sortColumn: "order",
                  sortBy: "asc",
                }}
                loading={action === "section-list" && requesting}
                data={customFieldSections || []}
                pagination={false}
                handleRowClick={(data) =>
                  navigate(
                    `${RouteConstants.APP_ROUTE_ADMINISTRATION_CUSTOM_FIELD_SECTIONS_EDIT.replace(
                      ":id",
                      data?.data?.id
                    )}`
                  )
                }
                selectionMode=""
                columns={columns}
              />
            </>
          )) ||
            null}
        </div>
      </div>
      <ConfirmDialog />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.self?.user,
  customFieldSections: state?.customFields?.data || [],
  action: state?.customFields?.action,
  requesting: state?.customFields?.requesting,
  error: state?.customFields?.error,
});

const mapDispatchToProps = (dispatch) => ({
  listSections: () => dispatch(sectionListRequest()),
  updateOrder: (payload) => dispatch(sectionUpdateOrderRequest(payload)),
  deleteSectionRequest: (payload) => dispatch(deleteSectionRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomFieldList);
